/**
 * Every AWS readiness flag that is set, drawn as its own callout.
 *
 * ## WHY IT IS A COMPONENT OF ITS OWN — SLICE 30
 *
 * This lived inline in `EmailTab`, and `EmailTab` reaches `lib/api/`, whose
 * import closure reaches `lib/config.ts` and `import.meta.env`. A verify suite
 * cannot render it, so `verify-integrations-screen` asserts this logic by
 * reading the SOURCE — which proves a string is present and proves nothing about
 * which branch renders it.
 *
 * The Overview drew the same flags from the same table and WAS render-tested,
 * with about thirty assertions on the `!null` trap below. Slice 30 removes that
 * duplicate, so this had to become the tested copy rather than inherit its
 * absence. Extracting a pure presentational module is how this codebase has
 * crossed that wall before — `AttachmentDeliveryNotice`, `CampaignFailureSections`,
 * `RecipientBadgeLegend`. It imports contracts and `@wend/ui` and nothing else.
 *
 * ## THE `!null` TRAP, WHICH IS THE WHOLE REASON FOR THE EXPLICIT COMPARISONS
 *
 * A key scoped to sending can call neither `GetAccount` nor
 * `ListEmailIdentities`. Then `sendingEnabled`, `sandboxed`,
 * `hasVerifiedIdentity` and both counts are all `null` — and `!null` is `true`.
 * Written with `!`, this component tells a tenant Amazon has paused their
 * account, or that they have nothing to send from, on evidence that was never
 * gathered. Every condition here is `=== true` or `=== false` for that reason,
 * and the suite walks the all-null shape past every one of them.
 */
import {
  SES_ACCOUNT_NOTHING_READABLE_COPY,
  SES_ACCOUNT_NOT_VISIBLE_COPY,
  SES_PRODUCTION_ACCESS_URL,
} from '@wend/shared';
import { Callout, buttonVariants } from '@wend/ui';
import { ExternalLink } from 'lucide-react';

import { pluralise } from '../lib/format';
import {
  SES_READINESS_DISPLAY,
  SES_SENDING_PAUSED,
  describeSandboxLimits,
  type SesReading,
} from '../lib/integration-display';

export function SesReadinessCallouts({
  readiness,
  check,
}: {
  readiness: NonNullable<SesReading['readiness']>;
  check: NonNullable<SesReading['check']>;
}) {
  return (
    <>
            {/* `=== false`, never `!`. Null means the key cannot call
                GetAccount, and `!null` is true — which would put "AWS has
                switched sending off" on an account nobody has been allowed
                to look at. That callout is the loudest red on this screen. */}
            {readiness.sendingEnabled === false && (
              <Callout tone={SES_SENDING_PAUSED.calloutTone} title={SES_SENDING_PAUSED.title}>
                The credentials are fine; the account is not. AWS has switched sending off, so
                nothing leaves this account — to a verified address or otherwise.
                {check.enforcementStatus !== null && (
                  <>
                    {' '}
                    Amazon reports its enforcement status as{' '}
                    <span className="font-mono text-[0.82rem] text-text">
                      {check.enforcementStatus}
                    </span>
                    .
                  </>
                )}{' '}
                Sending has to be reinstated by AWS Support before a Wend campaign can deliver
                anything.
              </Callout>
            )}

            {/* `=== false`, never `!`. Null means the listing was refused,
                and `!null` is true — so the plain negation would tell a
                tenant their account "has none" and send them to the SES
                console to verify a sender that is already there. This
                sentence asserts a fact about the account; it may only be
                rendered when the account was actually read. */}
            {readiness.hasVerifiedIdentity === false && (
              <Callout
                tone={SES_READINESS_DISPLAY.no_verified_identity.calloutTone ?? 'warn'}
                title={SES_READINESS_DISPLAY.no_verified_identity.title}
              >
                Amazon SES will only send from an address or a domain that has been verified on
                the account, and this one has none — so there is nothing for a campaign to send{' '}
                <em className="not-italic text-text">from</em>. Verify a sender under Identities in
                the Amazon SES console, then test again here.
              </Callout>
            )}

            {/*
              Verified is not usable. AWS pauses individual identities, and an
              account whose only verified sender is paused delivers nothing —
              but it is a different repair from having verified nothing at
              all, so it gets its own message rather than being folded into
              the one above. Without this the card showed no warning at all.
            */}
            {/* `=== true` / `=== false`, not `&&` and `!`. Both flags are null
                when the listing was refused, and while `null &&` happens to be
                falsy here, `!null` is true — so the shape only worked by
                accident and would break the moment the two were reordered.
                The explicit null test is also what narrows the count below. */}
            {readiness.hasVerifiedIdentity === true &&
              readiness.hasSendableIdentity === false &&
              readiness.verifiedIdentityCount !== null && (
                <Callout
                  tone="warn"
                  title={
                    readiness.verifiedIdentityCount === 1
                      ? 'The only verified sender is paused'
                      : 'Every verified sender is paused'
                  }
                >
                  This account has{' '}
                  {pluralise(readiness.verifiedIdentityCount, 'verified sender')}, but AWS
                  has paused sending for{' '}
                  {readiness.verifiedIdentityCount === 1 ? 'it' : 'all of them'} — so a
                  campaign would deliver nothing. This is usually a bounce or complaint rate that
                  crossed a threshold. Open Identities in the Amazon SES console to see which, fix
                  the underlying issue with AWS, then test again here.
                </Callout>
              )}

            {/* Two callouts, because only one of them is an observation.
                `sandboxed` is tri-state now: true when AWS said so, false
                when AWS said otherwise, and null when the key could not
                call GetAccount. Rendering null through the sentence below
                would tell somebody their account IS sandboxed on evidence
                that was never gathered. */}
            {/*
              SLICE 30 FIXED A DEFECT HERE THAT THE OVERVIEW HAD ALREADY FIXED.
              
              This printed `SES_ACCOUNT_NOT_VISIBLE_COPY.detail` for every
              unreadable account. That sentence opens *"This key can still list
              identities"* — which is FALSE for a key that can call neither
              `GetAccount` nor `ListEmailIdentities`, and this tab renders the
              identity list a few inches below saying exactly that. A tenant was
              told their key could do something the same screen showed it could
              not.
              
              `SendabilityCard` fixed it in slice 24 and recorded, in a comment,
              that the Email tab was still wrong. It was still wrong for six
              slices, because the note lived beside the fixed copy rather than
              beside the broken one. The condition is the contract's own:
              `sendableIdentityCount === null` means nothing could be listed.
            */}
            {readiness.accountVisible === false && (
              <Callout tone="info" title={SES_ACCOUNT_NOT_VISIBLE_COPY.short}>
                {readiness.sendableIdentityCount === null
                  ? SES_ACCOUNT_NOTHING_READABLE_COPY.detail
                  : SES_ACCOUNT_NOT_VISIBLE_COPY.detail}
              </Callout>
            )}

            {readiness.sandboxed === true && (
              <Callout
                tone={SES_READINESS_DISPLAY.sandboxed.calloutTone ?? 'warn'}
                title={SES_READINESS_DISPLAY.sandboxed.title}
                action={
                  <a
                    href={SES_PRODUCTION_ACCESS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                  >
                    <ExternalLink aria-hidden="true" className="size-4" />
                    How to request production access
                  </a>
                }
              >
                While an account is in the sandbox AWS holds it to{' '}
                <span className="text-text">
                  {describeSandboxLimits(check.quota)}
                </span>
                , and — the part that costs a campaign — it delivers{' '}
                <span className="text-text">only to addresses that are themselves verified</span>{' '}
                in SES. Every other recipient is rejected. Sending an ordinary list from a
                sandboxed account therefore reaches almost nobody, and the campaign will look like
                it ran. Production access is a request to AWS and usually takes about a day.
              </Callout>
            )}
    </>
  );
}
